import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScreenSlider } from 'src/components/common/ScreenSlider';
import { ROOT } from 'src/constants/routes';
import { prayerHubs as prayerHubsApi } from 'src/store/api/prayer-hubs';
import { Scroller } from 'src/utils/scroller';

import { CREDITS_SLIDE } from './CreditsSlide';
import { FeedSlide } from './FeedSlide';
import css from './PrayerHubsFeed.module.scss';

interface Props {
  scroller: Scroller;
  className?: string;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  active?: boolean;
  onDeactivate: () => unknown;
}

const FETCH_SIZE = 100;
const DEFAUL_DATA: any = [];

const PrayerHubsFeed = (props: Props) => {
  const { scroller, active: isActive = false, className = '', onDeactivate } = props;
  const location = useLocation();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(location.search);

  const {
    data: paginateRes,
    isFetching: isPaginateFetching,
    isLoading: isPaginateLoading,
  } = prayerHubsApi.endpoints.getPrayerHubs.useQuery({ limit: FETCH_SIZE, offset: 0 });

  const hubsData = paginateRes?.success ? paginateRes.data.items : DEFAUL_DATA;
  const hubsTotal = (paginateRes?.success ? paginateRes.data.page.total : 0) ?? 0;
  const isBusy = isPaginateFetching || isPaginateLoading;
  const sliderTotal = hubsTotal > 0 ? hubsTotal + 1 : 0;

  const sliderItems = React.useMemo(() => {
    if (hubsTotal === 0) return hubsData;
    return [...hubsData, CREDITS_SLIDE];
  }, [hubsData, hubsTotal]);

  const rawPage = Number(urlParams.get('page')) || 1;
  const page = sliderTotal > 0 ? Math.min(Math.max(rawPage, 1), sliderTotal) : rawPage;

  const onSliderPageChange = React.useCallback(
    (page: number) => {
      const urlParams = new URLSearchParams(location.search);

      urlParams.set('page', String(page));
      navigate(`${ROOT}?${urlParams.toString()}`);
    },
    [location, navigate]
  );
  const onSliderDeactivate = React.useCallback(() => {
    const urlParams = new URLSearchParams(location.search);

    urlParams.delete('page');
    navigate(`${ROOT}?${urlParams.toString()}`);

    onDeactivate();
  }, [location, navigate, onDeactivate]);

  const NoResults = React.useMemo(
    () =>
      function NoResults() {
        return (
          <div className={css.noResults}>
            <p className={css.noResultsText}>No data found.</p>
          </div>
        );
      },
    []
  );

  return (
    <ScreenSlider
      className={className}
      items={sliderItems}
      page={page}
      total={sliderTotal}
      scroller={scroller}
      active={isActive}
      busy={isBusy}
      destroyOnReachingTop
      containerRef={props.containerRef}
      slide={FeedSlide}
      noResults={NoResults}
      onPageChange={onSliderPageChange}
      onDeactivate={onSliderDeactivate}
    />
  );
};

export default PrayerHubsFeed;
